/**
 * Typed shapes for every entity this app's `PlatformApiClient` (see `PlatformApiClient.ts`)
 * deals in. Originally modeled from `system_architecture.md` §9's spec before any backend
 * existed; now reconciled against the REAL backend (`backend/app/schemas/platform.py`, read
 * directly, not summarized) now that `/api/v1/platform/*` is real. Where the real backend
 * genuinely can't back a field yet (`Tenant` gained plan_code/storage_quota_bytes/
 * branch_limit/ecommerce_enabled *columns* in the 2026-07-13 pass, but no platform response
 * schema surfaces them yet — verified directly, not assumed; separately, no MinIO-derived
 * storage byte counts exist anywhere), the field is typed `| null` rather than removed — the
 * mock client still fills it in (so the mock demo stays fully featured), and
 * `realPlatformApiClient.ts` returns `null` for it, documented per-field below and in that
 * file. See this app's ARCHITECTURE.md's "What's genuinely live vs. mocked" table for the
 * exact list.
 *
 * Where a shape genuinely reuses backend concepts (a tenant's identity fields, money/ids),
 * it imports from `@shop-ledger/shared-types` rather than redeclaring them.
 */
import type { ISODateTimeString, UUID } from "@shop-ledger/shared-types";

// ---------------------------------------------------------------------------------
// §9.1 — Auth (separate realm, MFA-enforced, aud: "platform" once real)
// ---------------------------------------------------------------------------------

export interface PlatformOperator {
  id: UUID;
  email: string;
  display_name: string;
  mfa_enrolled: boolean;
}

/**
 * A signed-in session. `access_token`/`refresh_token` are real `aud: "platform"` JWTs when
 * issued by `realPlatformApiClient` (`POST /platform/auth/mfa/verify`'s
 * `PlatformTokenPairResponse`) — `refresh_token` is optional because the mock client has no
 * concept of refresh at all (its `access_token` is just an opaque random string with no
 * cryptographic guarantee, and it never expires mid-demo in a way that needs rotating).
 * `realPlatformApiClient.ts` always sets `refresh_token`; treat its absence as "this session
 * came from the mock client."
 */
export interface PlatformSession {
  operator: PlatformOperator;
  access_token: string;
  refresh_token?: string;
  expires_at: ISODateTimeString;
}

export interface MfaChallenge {
  challenge_id: string;
  /**
   * Demo convenience ONLY, and ONLY ever present when `mockPlatformApiClient` issued this
   * challenge. The real backend's `PlatformLoginChallengeResponse`
   * (`backend/app/schemas/platform.py`) carries no such field at all — a real MFA
   * implementation must never return the expected code to the client that's supposed to
   * prove it doesn't already have it, so `realPlatformApiClient.ts` never sets this.
   * Optional (not removed) so the mock can keep exercising a two-step login UI end-to-end in
   * local dev without a real TOTP authenticator app. `MfaPage`/`PlatformAuthContext` render
   * it only when present, as an explicit "demo mode" hint, never silently auto-filled.
   */
  demo_code?: string;
}

// ---------------------------------------------------------------------------------
// §9.2 — Business onboarding
// ---------------------------------------------------------------------------------

/** Plan/quota tiers assigned at onboarding (§9.2 step 3) — stored on the tenant, not
 * hardcoded per deployment, per the spec's explicit instruction. */
export type PlanCode = "TRIAL" | "STARTER" | "GROWTH" | "ENTERPRISE";

export const PLAN_CODES: readonly PlanCode[] = ["TRIAL", "STARTER", "GROWTH", "ENTERPRISE"];

export interface PlanDefaults {
  storage_quota_gb: number;
  branch_limit: number;
  ecommerce_enabled: boolean;
}

/** `ecommerce_enabled` defaults `true` on every tier — a new business should never launch
 * with its online store off with no clue where to turn it on (real business-owner
 * confusion this caused before this default was normalized across tiers). The platform
 * admin can still flip it off per-tenant in the wizard's review step, same as any other
 * field here. */
export const PLAN_DEFAULTS: Record<PlanCode, PlanDefaults> = {
  TRIAL: { storage_quota_gb: 1, branch_limit: 1, ecommerce_enabled: true },
  STARTER: { storage_quota_gb: 10, branch_limit: 3, ecommerce_enabled: true },
  GROWTH: { storage_quota_gb: 50, branch_limit: 10, ecommerce_enabled: true },
  ENTERPRISE: { storage_quota_gb: 500, branch_limit: 100, ecommerce_enabled: true },
};

/**
 * What the onboarding wizard collects across its three steps (business info → first
 * branch/owner → plan/quota). Deliberately shaped as a near-superset of the REAL
 * `TenantBootstrapRequest` (`@shop-ledger/shared-types`, backing the real
 * `POST /auth/tenants/bootstrap` route) for the fields that endpoint already accepts
 * (`business_name`, `business_type`, `currency`, `branch_name`, `owner_email`) plus the
 * plan/quota fields §9.2 step 3 requires that no current endpoint accepts at all — see
 * `mockPlatformApiClient.ts`'s doc comment on `createTenant` for exactly how this maps.
 */
export interface OnboardingSubmission {
  business_name: string;
  business_type: string | null;
  currency: string;
  branch_name: string;
  owner_email: string;
  plan_code: PlanCode;
  storage_quota_gb: number;
  branch_limit: number;
  ecommerce_enabled: boolean;
}

export interface OnboardingChecklist {
  tenant_id: UUID;
  first_branch_created: boolean;
  first_product_added: boolean;
  first_sale_recorded: boolean;
  updated_at: ISODateTimeString;
}

/**
 * The result of `PlatformApiClient.createTenant` — a real business-owner walkthrough
 * follow-up (2026-07-13): `POST /platform/tenants` no longer accepts (or needs) an
 * operator-supplied `owner_password` — it issues a real one-time credential-setup token for
 * the new OWNER instead, the same invite mechanism `POST /users` uses (see
 * `backend/app/schemas/platform.py::PlatformTenantCreateResponse`, read directly). This
 * type carries that token through to the wizard so the operator can hand it to the business
 * owner — no real email sending exists in this backend, `note` says so explicitly either
 * way (real backend copy vs. this mock's own honest equivalent).
 */
export interface TenantCreationResult {
  tenant: PlatformTenantDetail;
  owner_user_id: UUID;
  owner_email: string;
  setup_token: string;
  setup_token_expires_at: ISODateTimeString;
  note: string;
}

/**
 * §9's "usage/statistics endpoint" gap (business-owner walkthrough finding: "platform admin
 * has no usage/statistics dashboard at all"). Real aggregate counts via
 * `GET /platform/stats` (`backend/app/schemas/platform.py::PlatformStatsOut`) — mirrored
 * field-for-field, including `sales_revenue_last_30d` as a DecimalString (this backend's
 * standard money-field wire convention, never a JS number).
 */
export interface PlatformStats {
  total_tenants: number;
  active_tenants: number;
  suspended_tenants: number;
  total_branches: number;
  total_users: number;
  sales_count_last_30d: number;
  sales_revenue_last_30d: string;
}

/**
 * The REAL, live-computed onboarding checklist — `GET /platform/tenants/{id}/checklist`
 * (`backend/app/schemas/platform.py::OnboardingChecklistOut`), computed from actual
 * branch/product/sale counts on every read, not a stored table. Distinct from the older
 * `OnboardingChecklist` type above, which models this app's own client-side "mark complete"
 * demo state machine (`domain/onboardingChecklist.ts`) — kept for the mock client's
 * interactive nicety (see that file's doc comment), but `LiveOnboardingChecklist` below is
 * now the source of truth both `TenantDetailPage` and the post-create wizard view render.
 */
export interface LiveOnboardingChecklistStep {
  step: string;
  label: string;
  completed: boolean;
  completed_at: ISODateTimeString | null;
  /** Progress counters for the steps with a numeric target (e.g. "7/10 products with
   * photos"). Both `null` for the pure boolean steps (business created, branding
   * customized). Added alongside the 3-step -> 6-step onboarding-checklist expansion. */
  current: number | null;
  target: number | null;
}

export interface LiveOnboardingChecklist {
  tenant_id: UUID;
  steps: LiveOnboardingChecklistStep[];
  all_complete: boolean;
}

/**
 * §9.1's real tenant-impersonation support-session, and §9's explicit UI requirement:
 * "must be time-boxed, logged, and visibly bannered in the UI ... so it can never be used
 * silently." Mirrors `backend/app/schemas/platform.py::ImpersonateResponse` field-for-field.
 * Deliberately no `refresh_token` — impersonation sessions are short-lived (15 min) and
 * non-renewable by design, both in the real backend and in this mock.
 */
export interface ImpersonationSession {
  access_token: string;
  token_type: string;
  expires_at: ISODateTimeString;
  impersonated_user_id: UUID;
  impersonated_user_email: string;
  tenant_id: UUID;
  /** Single-use code for `POST /auth/impersonation/consume` — `TenantDetailPage` opens
   * `{DASHBOARD_URL}/impersonate?code=<this>` in a new tab right after starting the
   * session, so the operator lands in a REAL, already-logged-in `dashboard-admin` tab
   * ready to do support work, not just this app's own "proof it works" view. */
  dashboard_handoff_code: string;
}

// ---------------------------------------------------------------------------------
// §9.3 — System health visualization
// ---------------------------------------------------------------------------------

export type HealthStatus = "healthy" | "degraded" | "down" | "unknown";

export interface HealthNode {
  id: string;
  label: string;
  status: HealthStatus;
  /** Human-readable current signal, e.g. "replication lag: 340ms" or the real /readyz
   * response body. */
  detail: string;
  /** True for every node except the two wired to the real `/healthz`/`/readyz` endpoints
   * (see `healthApi.ts` and this app's ARCHITECTURE.md "what's real vs. mocked" table). */
  mocked: boolean;
  lastChecked: ISODateTimeString;
}

// ---------------------------------------------------------------------------------
// §9.4 — Centralized logs
// ---------------------------------------------------------------------------------

export type LogLevel = "debug" | "info" | "warn" | "error";

export const LOG_LEVELS: readonly LogLevel[] = ["debug", "info", "warn", "error"];

/** §9.4's exact correlation-key set: "event_id, tenant_id, device_id, request_id". */
export interface LogEntry {
  event_id: UUID;
  tenant_id: UUID | null;
  device_id: string | null;
  request_id: UUID;
  timestamp: ISODateTimeString;
  level: LogLevel;
  message: string;
}

export interface LogFilter {
  tenantId?: string;
  level?: LogLevel;
  query?: string;
  /** Only entries at or after this ISO timestamp. */
  since?: ISODateTimeString;
}

// ---------------------------------------------------------------------------------
// §9.5 — Operational queues
// ---------------------------------------------------------------------------------

export interface SyncConflictQueueItem {
  id: UUID;
  tenant_id: UUID;
  tenant_name: string;
  entity_type: string;
  entity_id: UUID;
  /** Mirrors `SyncConflictStatus` from `@shop-ledger/shared-types` (kept as `string` here
   * since this is mock data modeling the *shape*, not a live read of that column). */
  status: string;
  created_at: ISODateTimeString;
}

export interface PaymentReconciliationQueueItem {
  id: UUID;
  tenant_id: UUID;
  tenant_name: string;
  sale_or_invoice_id: UUID;
  amount: string; // DecimalString, same wire convention as the rest of the backend
  status: "PENDING";
  created_at: ISODateTimeString;
}

export interface StorageQuotaWarning {
  tenant_id: UUID;
  tenant_name: string;
  storage_used_bytes: number;
  storage_quota_bytes: number;
}

/** §8's "alerting thresholds" — real proactive counts of items past the staleness
 * threshold, backing `PlatformNav`'s "Operational Queues" badge (`GET /platform/queues/
 * alert-summary`, `backend/app/schemas/platform.py::QueueAlertSummaryOut`). Previously a
 * documented gap: the queue list routes had real timestamps, but nothing counted a
 * backlog before an operator opened the Queues page. */
export interface QueueAlertSummary {
  threshold_minutes: number;
  sync_conflicts_stale_count: number;
  payment_pending_stale_count: number;
}

// ---------------------------------------------------------------------------------
// §9.6 — Tenant management
// ---------------------------------------------------------------------------------

export interface PlatformTenantSummary {
  id: UUID;
  business_name: string;
  business_type: string | null;
  currency: string;
  is_suspended: boolean;
  /** Live demo tenant marker (`backend/app/schemas/platform.py`'s docstring, "Demo
   * tenant" section) — at most one tenant ever has this true. Drives the "Reset Demo
   * Data" button (`TenantDetailPage.tsx`) and the list-page "DEMO" badge. */
  is_demo: boolean;
  /** `null` when read from `realPlatformApiClient` — `Tenant` gained a real `plan_code`
   * column in the 2026-07-13 pass, but `PlatformTenantSummary` (`backend/app/schemas/
   * platform.py`, read directly) doesn't read/return it yet, confirmed by that class's own
   * docstring. The mock client always fills this in. UI should render a `MockBadge` "not
   * available" in the `null` case, never fabricate a plan. */
  plan_code: PlanCode | null;
  created_at: ISODateTimeString;
  /** Businesses list activity signal — `null` means this business has never recorded a
   * sale at all (a genuinely distinct state from "long ago"), never fabricated as a
   * fake recent/old timestamp. "Active" (BusinessesListPage) means a sale within the
   * last 48 hours — this is a business-activity signal, distinct from `is_suspended`
   * (an admin action). */
  last_transaction_at: ISODateTimeString | null;
  /** DecimalStrings (this backend's standard money-field wire convention), one
   * business's own currency (`currency` above) — never summed across businesses since
   * they may use different currencies. */
  turnover_all_time: string;
  turnover_last_30d: string;
  /** Whether this tenant has finished every step of the 6-step onboarding checklist
   * (`LiveOnboardingChecklist` above) — lets the Businesses list surface "which
   * businesses are actually ready for business" without a click into each tenant. */
  onboarding_complete: boolean;
}

export interface PlatformTenantUsage {
  branch_count: number;
  active_users: number;
  /** `null` when read from `realPlatformApiClient` — no MinIO admin API integration exists
   * to compute a real byte count (see `PlatformTenantUsage`'s docstring in
   * `backend/app/schemas/platform.py`). The mock client always fills this in. */
  storage_used_bytes: number | null;
  storage_quota_bytes: number | null;
  sync_events_last_30d: number;
}

export interface PlatformTenantDetail extends PlatformTenantSummary {
  usage: PlatformTenantUsage;
  /** `null` when read from `realPlatformApiClient` — no onboarding-checklist table exists
   * server-side at all (`PlatformTenantDetail`'s docstring in
   * `backend/app/schemas/platform.py` confirms this explicitly). The mock client always
   * fills this in with a real (mock) checklist state. UI must treat `null` as "checklist
   * tracking isn't available for this tenant," not as "nothing done yet" — those are
   * different facts. */
  checklist: OnboardingChecklist | null;
}

export interface BackupTrigger {
  backup_id: UUID;
  tenant_id: UUID;
  triggered_at: ISODateTimeString;
  /** `"QUEUED"` is the mock client's literal. The real backend's
   * `PlatformBackupTriggerResponse` always returns `"ACKNOWLEDGED"` — deliberately NOT
   * `"QUEUED"`, specifically so nothing about the response could be misread as "a real
   * backup job now exists somewhere" (it's an explicit stub — writes one audit-log row,
   * triggers no actual backup). UI copy must not claim a job was queued for either value. */
  status: "QUEUED" | "ACKNOWLEDGED";
}

/** `POST /platform/tenants/{id}/reset-demo-data` — wipes and reseeds the live demo
 * tenant's catalog/sales/customer data (`backend/app/services/demo_service.py`). Backend
 * 403s if the target tenant isn't `is_demo`, so this is only ever called from a button
 * gated on that flag (`TenantDetailPage.tsx`). */
export interface DemoResetResult {
  tenant_id: UUID;
  reset_at: ISODateTimeString;
}

// ---------------------------------------------------------------------------------
// §9.1 — audit log (mocked). Not a task-brief-mandated screen, but every mutation this
// app's mock client performs (suspend/reactivate/backup/onboarding) is recorded here so
// the "every platform-admin action is written to an append-only audit log" requirement is
// at least modeled end-to-end in the mock layer, not silently dropped. Surfaced read-only
// in the Tenant Detail page.
// ---------------------------------------------------------------------------------

export interface PlatformAuditLogEntry {
  id: UUID;
  operator_id: UUID;
  operator_email: string;
  action: string;
  target_tenant_id: UUID | null;
  timestamp: ISODateTimeString;
  /** The mock client's shape: one human-readable summary string. The real backend
   * (`PlatformAuditLogEntryOut`) has no such field — it carries `before_state`/`after_state`
   * JSONB objects instead (see below). `realPlatformApiClient` always sets this to `null`;
   * `AuditLogPage` derives a display string from `before_state`/`after_state` when `detail`
   * is `null`. */
  detail: string | null;
  /** Real backend only (`realPlatformApiClient` sets these; the mock leaves both
   * `undefined`, distinct from the real client's explicit `null` for "this action recorded
   * no before-state"). JSONB — shape varies per `action`. */
  before_state?: Record<string, unknown> | null;
  after_state?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------------
// §16 — unified cross-tenant marketplace orders (platform-admin fulfillment view).
// Field names mirror `backend/app/schemas/marketplace.py` exactly (read directly, not
// summarized) — no renamed/re-cased fields. Unlike several §9.6 tenant fields above,
// nothing here is permanently mocked: `realPlatformApiClient`'s marketplace methods hit
// genuinely real `/platform/marketplace/*` routes, so no `MockBadge` is warranted on
// these pages regardless of which client is active (same "both clients are equally
// real" status as `listTenants`/the queues panels that also carry no badge).
// ---------------------------------------------------------------------------------

/** Live-recomputed rollup across every suborder's real `Shipment.status`
 * (`backend/app/services/marketplace_service.py::compute_rollup_status`, read directly)
 * — never a stored/trusted column. Exactly these four values: no suborders yet or every
 * suborder still PENDING -> `"PENDING"`; every suborder DELIVERED -> `"DELIVERED"`; any
 * suborder FAILED -> `"FAILED"`; any other mix of PACKED/SHIPPED/DELIVERED/PENDING ->
 * `"PROCESSING"`. */
export type MarketplaceOrderRollupStatus = "PENDING" | "PROCESSING" | "DELIVERED" | "FAILED";

export const MARKETPLACE_ORDER_ROLLUP_STATUSES: readonly MarketplaceOrderRollupStatus[] = [
  "PENDING",
  "PROCESSING",
  "DELIVERED",
  "FAILED",
];

/** `Shipment.status`'s own value set (`backend/app/services/shipping_service.py`) — the
 * legal-transition graph (`VALID_TRANSITIONS`) forward-only plus a terminal FAILED escape
 * hatch is enforced server-side; an illegal transition 409s (see
 * `PlatformApiClient.updateMarketplaceSuborderStatus`'s doc comment below). */
export type ShipmentStatus = "PENDING" | "PACKED" | "SHIPPED" | "DELIVERED" | "FAILED";

export const SHIPMENT_STATUSES: readonly ShipmentStatus[] = ["PENDING", "PACKED", "SHIPPED", "DELIVERED", "FAILED"];

export interface PlatformMarketplaceOrderSummary {
  id: UUID;
  contact_name: string;
  contact_email: string;
  grand_total: string;
  currency: string | null;
  created_at: ISODateTimeString;
  status: MarketplaceOrderRollupStatus;
  tenant_count: number;
}

export interface MarketplaceOrderLineItem {
  inventory_item_id: UUID;
  name: string;
  quantity: string;
  unit_price: string;
  total_price: string;
}

export interface PlatformMarketplaceSuborderDetail {
  tenant_id: UUID;
  tenant_name: string;
  sale_id: UUID;
  suborder_id: UUID;
  shipment_id: UUID;
  sub_total: string;
  grand_total: string;
  items: MarketplaceOrderLineItem[];
  shipment_status: ShipmentStatus;
  carrier: string;
  tracking_number: string | null;
  shipped_at: ISODateTimeString | null;
  delivered_at: ISODateTimeString | null;
}

export interface PlatformMarketplaceOrderDetail {
  id: UUID;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  region: string | null;
  postal_code: string | null;
  country: string;
  grand_total: string;
  currency: string | null;
  created_at: ISODateTimeString;
  status: MarketplaceOrderRollupStatus;
  suborders: PlatformMarketplaceSuborderDetail[];
}

/** `GET /platform/marketplace/orders` query params — all optional. `since`/`until` are
 * plain `YYYY-MM-DD` date strings (the backend's `date` query-param type), not full
 * timestamps. `status` filters client-visible rows in Python against the live-recomputed
 * rollup (see `PlatformMarketplaceOrderSummary.status`'s doc comment) — the backend does
 * the same post-hoc filtering rather than a SQL WHERE clause, since the rollup isn't a
 * real column. */
export interface MarketplaceOrderFilters {
  status?: MarketplaceOrderRollupStatus;
  since?: string;
  until?: string;
  limit?: number;
}

/** `PATCH /platform/marketplace/orders/{orderId}/suborders/{suborderId}` body — mirrors
 * `MarketplaceSuborderStatusUpdate` (`backend/app/schemas/marketplace.py`) field-for-field.
 * All fields optional/partial-update; omitting a field leaves that column untouched. */
export interface MarketplaceSuborderStatusUpdateInput {
  status?: ShipmentStatus;
  carrier?: string;
  tracking_number?: string;
}

/**
 * §9.7 General Settings — platform-wide outbound SMTP config
 * (`backend/app/schemas/platform.py::PlatformSettingsOut`). Never carries the actual
 * password — `smtp_password_set` is the only signal a GET response gives about it, same
 * spirit as this app never receiving a `PlatformOperator`'s `hashed_password`/`mfa_secret`.
 */
export interface PlatformGeneralSettings {
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_username: string | null;
  smtp_password_set: boolean;
  smtp_from_email: string | null;
  smtp_from_name: string | null;
  smtp_use_tls: boolean;
}

/** `PATCH /platform/settings/general` body — every field optional/partial-update.
 * Omitting `smtp_password` leaves the currently-stored password untouched; send
 * `smtp_password: ""` explicitly to clear it. */
export interface PlatformGeneralSettingsUpdate {
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_password?: string;
  smtp_from_email?: string;
  smtp_from_name?: string;
  smtp_use_tls?: boolean;
}

/** `POST /platform/settings/general/test-email` response — always 200, even on failure;
 * `sent: false` + a human-readable `detail` is how a misconfiguration is reported, not an
 * HTTP error status. Sends against whatever is currently SAVED, not unsaved form state. */
export interface SendTestEmailResult {
  sent: boolean;
  detail: string;
}
